import { BarChart3, Shield, Rocket, Bot, type LucideIcon } from "lucide-react";
import type { AgentSeverity, AgentRunStatus } from "@/api/agents";

export interface AgentMeta {
  icon: LucideIcon;
  scheduleLabel: string;
  accent: string;
}

export const AGENT_META: Record<string, AgentMeta> = {
  atlas: {
    icon: BarChart3,
    scheduleLabel: "Weekly · Mon 8 AM",
    accent: "text-accent",
  },
  sentinel: {
    icon: Shield,
    scheduleLabel: "Daily · 6 AM",
    accent: "text-warning",
  },
  pilot: {
    icon: Rocket,
    scheduleLabel: "On new hire",
    accent: "text-success",
  },
};

export function agentIcon(name: string): LucideIcon {
  return AGENT_META[name]?.icon ?? Bot;
}

export function agentScheduleLabel(name: string, cron: string | null): string {
  if (AGENT_META[name]) return AGENT_META[name].scheduleLabel;
  return cron ? `Cron · ${cron}` : "Event-triggered";
}

export function severityVariant(
  severity: AgentSeverity
): "info" | "warning" | "danger" {
  if (severity === "critical") return "danger";
  if (severity === "warning") return "warning";
  return "info";
}

export function severityDotClass(severity: AgentSeverity): string {
  if (severity === "critical") return "bg-danger";
  if (severity === "warning") return "bg-warning";
  return "bg-accent";
}

export function runStatusVariant(
  status: AgentRunStatus
): "success" | "warning" | "danger" | "info" {
  if (status === "success") return "success";
  if (status === "failed") return "danger";
  return "info";
}

export function runStatusLabel(
  status: AgentRunStatus,
  output?: Record<string, unknown> | null
): string {
  if (status === "failed") return "Failed";
  if (status === "running") return "Running";
  // Success — if the output summary carries any alerts / findings, flag it
  if (output && typeof output === "object") {
    const obj = output as Record<string, unknown>;
    const alerts = obj.alerts_count ?? obj.findings_count ?? obj.critical_count;
    if (typeof alerts === "number" && alerts > 0) return "Issues found";
  }
  return "Completed";
}

export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return "—";
  // Backend emits naive UTC datetimes (no "Z" / no offset). JS would parse
  // those as local time, which makes every recent row look like "just now"
  // in any non-UTC timezone. Force UTC when no zone marker is present.
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
  const normalized = hasZone ? iso : `${iso}Z`;
  const then = new Date(normalized).getTime();
  if (Number.isNaN(then)) return "—";
  const diff = Math.max(0, (Date.now() - then) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
